import * as React from "react";
import { useState } from "react";
import { Tooltip, Checkbox } from "@mui/material";
import useDrive from "../../hooks/useDrive";
import useNotification from "../../hooks/useNotification";
import DriveService from "../../services/DriveService";
import FileService from "../../services/FileService";
import { DRIVE_TYPES } from "../../config/appConst";
import { FileThumbnail, getRowMeta } from "../FileThumbnail";
import styles from "./fileAndFolderTbl.module.css";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";

export const FolderContentGrid = ({ rows, selectMode, selected, onToggle }) => {
  const [dragOverId, setDragOverId] = useState(null);
  const { CurrentDrive, changeCurrentDrive, changeSelectedRow, changePreviewObject, changeRenameFolder, changeRenameRow } = useDrive();
  const { addNotification } = useNotification();

  const handleRename = (e, row) => {
    e.stopPropagation();
    if (row.type === 'folder') changeRenameFolder(row);
    else changeRenameRow(row);
  };

  const openFolder = async row => {
    const { parent, driveType } = CurrentDrive;
    const path = parent ? `${parent}/${row.Key}` : row.Key;
    const res = await DriveService.getFileTree(path, driveType);
    const cur = { parent: path, driveType };
    if (driveType === DRIVE_TYPES.SHARED) cur.sharedDrive = res; else cur.drive = res;
    changeCurrentDrive(cur);
  };

  const openFile = row => {
    changeSelectedRow(row);
    changePreviewObject(row);
  };

  /* ── Drag handlers ── */
  const handleDragStart = (e, row) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify({
      key: row.Key,
      type: row.type,
      parent: CurrentDrive.parent,
      driveType: CurrentDrive.driveType,
    }));
  };

  const handleDragOver = (e, row) => {
    if (row.type !== "folder") return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverId(row.id);
  };

  const handleDragLeave = () => setDragOverId(null);

  const handleDrop = async (e, targetRow) => {
    e.preventDefault();
    setDragOverId(null);
    if (targetRow.type !== "folder") return;

    let payload;
    try { payload = JSON.parse(e.dataTransfer.getData("application/json")); }
    catch { return; }
    if (payload.key === targetRow.Key) return;

    const { driveType } = CurrentDrive;
    const destFolder = CurrentDrive.parent
      ? `${CurrentDrive.parent}/${targetRow.Key}`
      : targetRow.Key;

    try {
      if (payload.type === "file") {
        const srcPath = payload.parent ? `${payload.parent}/${payload.key}` : payload.key;
        const dstPath = `${destFolder}/${payload.key}`;
        await FileService.copy({ file_name: srcPath, file_name_new: dstPath, move: 1, driveType });
      } else {
        const srcFolder = payload.parent ? `${payload.parent}/${payload.key}` : payload.key;
        await DriveService.copy({ folder: srcFolder, folder_new: destFolder, move: 1, driveType });
      }
      addNotification({ type: "success", message: `"${payload.key}" moved into "${targetRow.Key}"` });
      const drive = await DriveService.getFileTree(CurrentDrive.parent, driveType);
      if (driveType === DRIVE_TYPES.SHARED)
        changeCurrentDrive({ ...CurrentDrive, drive: {}, sharedDrive: drive });
      else
        changeCurrentDrive({ ...CurrentDrive, drive, sharedDrive: {} });
    } catch {
      addNotification({ type: "error", message: "Move failed" });
    }
  };

  return (
    <div className={styles.grid}>
      {rows.map(row => {
        const { bg, color, label } = getRowMeta(row);
        const isSelected = selected?.has(row.id);
        const isDropTarget = dragOverId === row.id && row.type === "folder";

        return (
          <div
            key={row.id}
            className={styles.card}
            draggable
            onDragStart={e => handleDragStart(e, row)}
            onDragOver={e => handleDragOver(e, row)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDrop(e, row)}
            onClick={() => {
              if (selectMode) { onToggle?.(row.id); return; }
              row.type === "folder" ? openFolder(row) : openFile(row);
            }}
            style={{
              border: isDropTarget
                ? '2px solid #7c6af7'
                : isSelected ? '1px solid #e85878' : undefined,
              background: isDropTarget
                ? 'rgba(124,106,247,0.14)'
                : isSelected ? 'rgba(232,88,120,0.06)' : undefined,
              transform: isDropTarget ? 'scale(1.03)' : undefined,
              transition: 'transform 0.12s, border 0.12s, background 0.12s',
            }}
          >
            {/* Checkbox — select mode only */}
            {selectMode && (
              <div
                className={`${styles.cardBtn} ${styles.cardBtnCheck} ${styles.cardBtnCheckVisible}`}
                onClick={e => { e.stopPropagation(); onToggle?.(row.id); }}
              >
                <Checkbox
                  checked={!!isSelected}
                  onChange={() => onToggle?.(row.id)}
                  onClick={e => e.stopPropagation()}
                  size="small"
                  sx={{ p: 0, color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#e85878' } }}
                />
              </div>
            )}

            {/* Rename — top right */}
            <Tooltip title="Rename" placement="top">
              <div
                className={`${styles.cardBtn} ${styles.cardBtnRename}`}
                onClick={e => handleRename(e, row)}
              >
                <DriveFileRenameOutlineIcon sx={{ fontSize: 15 }} />
              </div>
            </Tooltip>

            {/* Drop indicator label */}
            {isDropTarget && (
              <div style={{
                position: 'absolute', inset: 0, borderRadius: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#7c6af7', fontWeight: 700, fontSize: 13, zIndex: 5,
                background: 'rgba(124,106,247,0.08)',
                pointerEvents: 'none',
              }}>
                Move here
              </div>
            )}

            {/* Icon / thumbnail */}
            <div className={styles.cardThumb}>
              <FileThumbnail row={row} fill />
            </div>

            {/* File name */}
            <Tooltip title={row.Key || ""} placement="bottom" arrow>
              <div className={styles.cardName}>{row.Key || "—"}</div>
            </Tooltip>

            {/* Badge + size */}
            <div className={styles.cardMeta}>
              <span className={styles.typeBadge} style={{ background: bg, color }}>
                {row.type === "folder" ? "Folder" : label || row.Key?.split(".").pop()?.toUpperCase()}
              </span>
              {row.Size && <span>{row.Size}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};
